import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { ChevronDown, Clock, History, Play, SlidersHorizontal } from "lucide-react";
import { ApiError } from "@/lib/api";
import { useRunScraper, useRunStatus, useScrapeJobs } from "@/lib/scraper";
import { CHANNELS_KEY, useChannels } from "@/lib/channels";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Alert } from "@/components/ui/alert";
import { StatusBadge } from "@/components/ui/badge";
import { ProgressBar } from "@/components/ui/progress";
import { SimpleSelect } from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
} from "@/components/ui/dialog";
import {
  Table,
  THead,
  TBody,
  TR,
  TH,
  TD,
} from "@/components/ui/table";
import { formatRelative, formatDateTime } from "@/lib/format";
import { cn } from "@/lib/cn";

/**
 * RunControls — the scraper "Параметры запуска" section + the persistent
 * RecentJobs mini-panel (DESIGN-SYSTEM §3 IA replacement for two `<details>`).
 *
 * Owns the run-options state + the run mutation (logic unchanged — same
 * `useRunScraper` payload as before); the page only renders it.
 */

/** Mirrors JOIN_APPROVAL_TIMEOUT_HOURS in backend/app/core/scraper.py. */
const JOIN_APPROVAL_TIMEOUT_HOURS = 24;

/** Runs are paced in minutes per channel, so polling faster buys nothing. */
const RUN_POLL_MS = 10_000;

/**
 * Refetch the channel list while a run is in flight. Channel rows change status
 * as the worker walks them, and a stale list makes a running scrape look stuck.
 */
function useChannelsAutoRefresh(active: boolean) {
  const qc = useQueryClient();
  useEffect(() => {
    if (!active) return;
    const id = setInterval(() => {
      void qc.invalidateQueries({ queryKey: CHANNELS_KEY });
    }, RUN_POLL_MS);
    return () => clearInterval(id);
  }, [active, qc]);
}
export function RunControls() {
  const { t } = useTranslation();
  const channels = useChannels();
  const run = useRunScraper();

  const [optionsOpen, setOptionsOpen] = useState(false);
  const [runMessage, setRunMessage] = useState<string | null>(null);
  const [runError, setRunError] = useState<string | null>(null);

  // Scrape tuning — defaults are inline, operator overrides per-run.
  const [includeReactionAuthors, setIncludeReactionAuthors] = useState(false);
  const [includeMentions, setIncludeMentions] = useState(false);
  const [sinceDays, setSinceDays] = useState<string>("");
  const [skipBots, setSkipBots] = useState(true);
  const [autoJoin, setAutoJoin] = useState(false);
  const [maxUsersPerChannel, setMaxUsersPerChannel] = useState<string>("5000");
  const [accounts, setAccounts] = useState<string>("1");

  const pendingJoinCount = (channels.data ?? []).filter(
    (c) => c.parsing_status === "pending_join",
  ).length;

  // While a run is in flight the channel list changes under us (queued →
  // parsing → done), so poll. Runs are paced in minutes, hence the slow
  // interval — this is a progress indicator, not a spinner.
  const status = useRunStatus(RUN_POLL_MS);
  const running = status.data?.running ?? false;
  const queuedCount = status.data?.queued_channels ?? 0;
  useChannelsAutoRefresh(running);

  const onRun = async () => {
    setRunMessage(null);
    setRunError(null);
    try {
      const r = await run.mutateAsync({
        mode: "append",
        include_reaction_authors: includeReactionAuthors,
        include_mentions: includeMentions,
        since_days: sinceDays ? Number(sinceDays) : null,
        skip_bots: skipBots,
        auto_join: autoJoin,
        accounts: Number(accounts) || 1,
        max_users_per_channel: maxUsersPerChannel
          ? Number(maxUsersPerChannel)
          : undefined,
      });
      setRunMessage(
        r.already_running
          ? t("users.run_already_running")
          : t("users.run_queued", { channels: r.channels }),
      );
    } catch (err) {
      setRunError(err instanceof ApiError ? err.detail : t("users.run_error"));
    }
  };

  return (
    <div className="space-y-3">
      <Card>
        <CardContent className="space-y-4 pt-6">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <span className="flex h-10 w-10 items-center justify-center rounded-full bg-accent/15 text-accent">
                <SlidersHorizontal size={18} strokeWidth={2} aria-hidden="true" />
              </span>
              <div>
                <p className="text-sm font-semibold text-fg-primary">
                  {t("users_redesign.run_section_title")}
                </p>
                <p className="text-xs text-fg-muted">
                  {running
                    ? t("users.run_in_progress", {
                        queued: queuedCount,
                        parsing: status.data?.parsing_channels ?? 0,
                      })
                    : t("users.queued_hint", { count: queuedCount })}
                </p>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setOptionsOpen((v) => !v)}
                iconRight={
                  <ChevronDown
                    size={15}
                    strokeWidth={2}
                    aria-hidden="true"
                    className={cn(
                      "transition-transform",
                      optionsOpen && "rotate-180",
                    )}
                  />
                }
              >
                {t("users_redesign.run_options_toggle")}
              </Button>
              <Button
                onClick={() => void onRun()}
                disabled={queuedCount === 0 || running}
                loading={run.isPending}
                iconLeft={<Play size={16} strokeWidth={2} aria-hidden="true" />}
              >
                {running
                  ? t("users.running")
                  : t("users.run", { count: queuedCount })}
              </Button>
            </div>
          </div>

          {optionsOpen && (
            <div className="space-y-4 border-t border-border pt-4">
              <p className="text-xs text-fg-muted">
                {t("users_redesign.run_section_hint")}
              </p>

              <Switch
                checked={includeReactionAuthors}
                onCheckedChange={setIncludeReactionAuthors}
                label={t("users.opt_reactions")}
                description={t("users.opt_reactions_hint")}
              />

              <Switch
                checked={includeMentions}
                onCheckedChange={setIncludeMentions}
                label={t("users.opt_mentions")}
                description={t("users.opt_mentions_hint")}
              />

              <Switch
                checked={skipBots}
                onCheckedChange={setSkipBots}
                label={t("users.opt_skip_bots")}
                description={t("users.opt_skip_bots_hint")}
              />

              <Switch
                checked={autoJoin}
                onCheckedChange={setAutoJoin}
                label={t("users.opt_auto_join")}
                description={t("users.opt_auto_join_hint")}
              />

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label htmlFor="opt_since_days">
                    {t("users.opt_since_days")}
                  </Label>
                  <Input
                    id="opt_since_days"
                    type="number"
                    min="1"
                    max="365"
                    value={sinceDays}
                    onChange={(e) => setSinceDays(e.target.value)}
                    placeholder="30"
                  />
                  <p className="text-xs text-fg-muted">
                    {t("users.opt_since_days_hint")}
                  </p>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="opt_accounts">{t("users.opt_accounts")}</Label>
                  <SimpleSelect
                    value={accounts}
                    onValueChange={setAccounts}
                    options={[
                      { value: "1", label: "1" },
                      { value: "2", label: "2" },
                      { value: "3", label: "3" },
                    ]}
                  />
                  <p className="text-xs text-fg-muted">
                    {t("users.opt_accounts_hint")}
                  </p>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="opt_max_users">
                    {t("users.opt_max_users")}
                  </Label>
                  <Input
                    id="opt_max_users"
                    inputMode="numeric"
                    value={maxUsersPerChannel}
                    onChange={(e) =>
                      setMaxUsersPerChannel(e.target.value.replace(/\D/g, ""))
                    }
                    placeholder="200"
                  />
                  <p className="text-xs text-fg-muted">
                    {t("users.opt_max_users_hint")}
                  </p>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {runMessage && (
        <Alert tone="success">{runMessage}</Alert>
      )}
      {runError && <Alert tone="danger">{runError}</Alert>}

      {pendingJoinCount > 0 && <PendingJoinPanel />}

      <RecentJobsPanel running={running} />
    </div>
  );
}

/**
 * PendingJoinPanel — channels whose join request is waiting on an admin.
 *
 * Without this the operator sees a channel sit in a non-terminal state with no
 * explanation. Each row shows how far into the approval window it is, so
 * "nothing is happening" is visibly distinct from "still within the deadline".
 */
function PendingJoinPanel() {
  const { t } = useTranslation();
  const channels = useChannels();

  const pending = (channels.data ?? []).filter(
    (c) => c.parsing_status === "pending_join",
  );
  if (pending.length === 0) return null;

  return (
    <Card>
      <CardContent className="space-y-3 pt-6">
        <div className="flex items-center gap-3">
          <span className="flex h-9 w-9 items-center justify-center rounded-full bg-info/15 text-info">
            <Clock size={17} strokeWidth={2} aria-hidden="true" />
          </span>
          <div>
            <p className="text-sm font-semibold text-fg-primary">
              {t("users.pending_join.title", { count: pending.length })}
            </p>
            <p className="text-xs text-fg-muted">
              {t("users.pending_join.hint", {
                hours: JOIN_APPROVAL_TIMEOUT_HOURS,
              })}
            </p>
          </div>
        </div>

        <ul className="space-y-2.5">
          {pending.map((c) => {
            const display = c.username
              ? `@${c.username}`
              : c.title ?? String(c.tg_channel_id ?? "—");
            // Elapsed share of the approval window. A missing timestamp (the
            // poller clears it on success) reads as "just asked" rather than
            // pinning the bar to a bogus value.
            const requestedMs = c.join_requested_at
              ? new Date(c.join_requested_at).getTime()
              : null;
            const elapsedHours =
              requestedMs != null
                ? (Date.now() - requestedMs) / 3_600_000
                : 0;
            const remainingHours = Math.max(
              0,
              JOIN_APPROVAL_TIMEOUT_HOURS - elapsedHours,
            );

            return (
              <li key={c.id} className="space-y-1">
                <div className="flex items-center justify-between gap-2">
                  <span className="truncate font-mono text-xs text-fg-secondary">
                    {display}
                  </span>
                  <span className="shrink-0 text-xs text-fg-muted">
                    {c.join_requested_at
                      ? t("users.pending_join.remaining", {
                          hours: Math.ceil(remainingHours),
                        })
                      : "—"}
                  </span>
                </div>
                <ProgressBar
                  value={Math.min(elapsedHours, JOIN_APPROVAL_TIMEOUT_HOURS)}
                  max={JOIN_APPROVAL_TIMEOUT_HOURS}
                  tone="info"
                  compact
                />
              </li>
            );
          })}
        </ul>
      </CardContent>
    </Card>
  );
}

/**
 * RecentJobsPanel — persistent mini status panel: the last job + its
 * StatusBadge, with a "Run history" button that opens the full list in a
 * Dialog (replaces the old RecentJobs `<details>`).
 */
function RecentJobsPanel({ running = false }: { running?: boolean }) {
  const { t } = useTranslation();
  // One job row per channel, written as the worker finishes each — polling
  // while a run is live is what makes progress visible.
  const jobs = useScrapeJobs(undefined, running ? RUN_POLL_MS : false);
  const [historyOpen, setHistoryOpen] = useState(false);

  const data = jobs.data ?? [];
  const last = data[0] ?? null;

  return (
    <>
      <Card>
        <CardContent className="flex flex-wrap items-center justify-between gap-3 py-3">
          <div className="flex min-w-0 items-center gap-3">
            <span className="text-xs font-medium uppercase tracking-wide text-fg-muted">
              {t("users_redesign.last_job")}
            </span>
            {last ? (
              <div className="flex flex-wrap items-center gap-2 text-sm">
                <StatusBadge
                  domain="scrape_job"
                  value={last.status}
                  label={t(`channels.status.${last.status}`, {
                    defaultValue: last.status,
                  })}
                  dot
                />
                <span className="font-mono text-xs text-fg-secondary">
                  {t("users_redesign.last_job_added", { count: last.users_added })}
                </span>
                <span className="text-xs text-fg-muted">
                  {formatRelative(last.created_at)}
                </span>
              </div>
            ) : (
              <span className="text-sm text-fg-muted">
                {t("users_redesign.last_job_none")}
              </span>
            )}
          </div>
          {data.length > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setHistoryOpen(true)}
              iconLeft={<History size={15} strokeWidth={2} aria-hidden="true" />}
            >
              {t("users_redesign.history_open")} ({data.length})
            </Button>
          )}
        </CardContent>
      </Card>

      <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
        <DialogContent size="lg">
          <DialogHeader>
            <DialogTitle>{t("users_redesign.history_title")}</DialogTitle>
          </DialogHeader>
          <DialogBody>
            {data.length === 0 ? (
              <p className="text-sm text-fg-muted">
                {t("users_redesign.history_empty")}
              </p>
            ) : (
              <Table>
                <THead>
                  <tr>
                    <TH>{t("users.jobs.col.status")}</TH>
                    <TH>{t("users.jobs.col.added")}</TH>
                    <TH>{t("users.jobs.col.skipped")}</TH>
                    <TH>{t("users.jobs.col.duration")}</TH>
                    <TH>{t("users.jobs.col.created")}</TH>
                  </tr>
                </THead>
                <TBody>
                  {data.map((j) => {
                    const dur =
                      j.started_at && j.finished_at
                        ? Math.round(
                            (new Date(j.finished_at).getTime() -
                              new Date(j.started_at).getTime()) /
                              1000,
                          )
                        : null;
                    return (
                      <TR key={j.id}>
                        <TD>
                          <StatusBadge
                            domain="scrape_job"
                            value={j.status}
                            label={j.status}
                            dot
                          />
                        </TD>
                        <TD className="font-mono text-sm">{j.users_added}</TD>
                        <TD className="font-mono text-sm">{j.users_skipped}</TD>
                        <TD className="font-mono text-sm text-fg-muted">
                          {dur != null ? `${dur}s` : "—"}
                        </TD>
                        <TD className="text-xs text-fg-muted">
                          {formatDateTime(j.created_at)}
                        </TD>
                      </TR>
                    );
                  })}
                </TBody>
              </Table>
            )}
          </DialogBody>
        </DialogContent>
      </Dialog>
    </>
  );
}
